import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
// NOTE: Quill CSS should be imported in src/index.css or src/App.css to avoid conflicts

/**
 * RichTextEditor - A React wrapper for Quill.js rich text editor
 *
 * @param {Object} props
 * @param {string} props.value - HTML content
 * @param {Function} props.onChange - Callback when content changes (receives HTML string)
 * @param {string} props.placeholder - Placeholder text (required, pass from parent)
 * @param {boolean} props.readOnly - Whether editor is read-only
 * @param {number} props.minHeight - Minimum height in pixels (default: 300)
 */
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  readOnly = false,
  minHeight = 200
}) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Initialize Quill once
  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    const container = containerRef.current;

    // Create editor div inside container
    const editorDiv = document.createElement('div');
    container.appendChild(editorDiv);

    // Create Quill instance
    const quill = new Quill(editorDiv, {
      theme: 'snow',
      readOnly,
      placeholder,
      modules: {
        toolbar: [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'align': [] }],
          ['link'],
          ['clean']
        ]
      }
    });

    // Set initial content
    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
    }

    // Listen for text changes
    quill.on('text-change', () => {
      if (isUpdatingRef.current) return;

      const html = quill.root.innerHTML;
      if (onChange) {
        onChange(html);
      }
    });

    quillRef.current = quill;

    // Cleanup
    return () => {
      if (container) {
        container.innerHTML = '';
      }
      quillRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update content when value prop changes
  useEffect(() => {
    if (!quillRef.current) return;

    const currentContent = quillRef.current.root.innerHTML;
    if (currentContent !== value) {
      isUpdatingRef.current = true;
      quillRef.current.clipboard.dangerouslyPasteHTML(value || '');
      isUpdatingRef.current = false;
    }
  }, [value]);

  // Update readOnly state
  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!readOnly);
    }
  }, [readOnly]);

  return (
    <div className="rich-text-editor-wrapper">
      <div
        ref={containerRef}
      />
      <style>{`
        .rich-text-editor-wrapper .ql-container {
          min-height: ${minHeight}px !important;
          height: auto !important;
          font-size: 14px;
        }
        .rich-text-editor-wrapper .ql-editor {
          min-height: ${minHeight}px !important;
          height: auto !important;
        }
        .rich-text-editor-wrapper .ql-toolbar.ql-snow {
          background-color: var(--bs-tertiary-bg);
          border: 1px solid #e9e9ef;
          border-radius: 5px 5px 0 0;
        }
        .rich-text-editor-wrapper .ql-container.ql-snow {
          border: 1px solid #e9e9ef;
          border-radius: 0 0 5px 5px;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
